import React, { useRef, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useCreatorPublishing } from '../hooks/useCreatorPublishing';
import { callDigitalAssets, continueIrysArchive, executePublishingOperation, formatAtomic, pendingPublishingOperation } from '../utils/digitalAssetsClient';
import MintProgress from './MintProgress';
import { getOfferMintStages } from '../utils/mintProgress';
export default function DigitalAssetPurchase({ comp, ownerPublicKey, compact = false, hideSummary = false, showMintProgress = false }) {
    const wallet = useWallet();
    const { offers } = useCreatorPublishing(ownerPublicKey);
    const offer = offers.find(item => String(item.componentId) === String(comp.id));
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [completedStages, setCompletedStages] = useState(0);
    const minting = useRef(false);
    const pendingId = offer ? pendingPublishingOperation(wallet.publicKey?.toBase58?.(), 'mint:' + offer.id) : '';
    const soldOut = offer?.editionMode !== 'OPEN' && offer?.mintedCount >= offer?.supply;
    if (offer?.mediaMint && comp.mediaMint?.enabled !== true) return null;
    if (!offer || offer.status !== 'PUBLISHED') return null;
    const buy = async () => {
        if (minting.current) return;
        minting.current = true;
        try {
            setBusy(true); setMessage('Preparing your collectible...'); setCompletedStages(0);
            const scope = 'mint:' + offer.id;
            const pending = pendingPublishingOperation(wallet.publicKey?.toBase58?.(), scope);
            let result = pending ? await callDigitalAssets('confirmOperation', { requestId: pending }, wallet) : null;
            if (!result || ['FAILED', 'EXPIRED'].includes(result.status)) {
                for (let attempt = 0; attempt < 15; attempt += 1) {
                    result = await callDigitalAssets('prepareMint', { offerId: offer.id }, wallet);
                    if (result.stage === 'IRYS_UPLOAD') result = await continueIrysArchive(wallet, result,
                        () => callDigitalAssets('prepareMint', { offerId: offer.id }, wallet), setMessage);
                    setCompletedStages(result.pending ? 1 : 3);
                    if (!result.pending) break;
                    setMessage(result.stage === 'ARCHIVING' ? 'Preparing media...' : 'Waiting for the current mint...');
                    await new Promise(resolve => setTimeout(resolve, 3000));
                }
            }
            if (result.requestId) {
                setCompletedStages(3);
                result = await executePublishingOperation(wallet, result, scope, message => {
                    setMessage(message);
                    setCompletedStages(message.startsWith('Approve ') ? 3 : 4);
                });
            }
            if (result.status === 'CONFIRMED') setCompletedStages(7);
            setMessage(result.status === 'CONFIRMED' ? 'Collectible minted to your wallet.' : 'Your purchase is still preparing. Retry to continue the same transaction.');
        } catch (error) { setMessage(error.message); } finally { minting.current = false; setBusy(false); }
    };
    return <div className="digital-asset-purchase" style={{ margin: '12px 0' }}>
        {!compact && !hideSummary && <p><strong>{offer.name}</strong> - {formatAtomic(offer.priceAtomic, offer.paymentDecimals)} {offer.paymentToken}
            {' - '}{offer.mintedCount}{offer.editionMode === 'OPEN' ? ' minted (open edition)' : '/' + offer.supply + ' minted'}</p>}
        <button type="button" className="muulti-button" disabled={busy || !wallet.connected || (soldOut && !pendingId)} onClick={buy}>
            {busy ? 'Minting...' : pendingId ? 'Continue mint' : soldOut ? 'Sold out'
                : compact ? 'Mint NFT - ' + formatAtomic(offer.priceAtomic, offer.paymentDecimals) + ' ' + offer.paymentToken : 'Collect Digital Asset'}
        </button>
        {offer.archiveProvider === 'IRYS' && offer.archiveStrategy === 'ARCHIVE_ON_FIRST_MINT' && offer.archiveStatus !== 'READY'
            && <small style={{ display: 'block', marginTop: 8 }}>Be the first to collect this piece. Your purchase also covers the one-time cost of publishing its files to Irys storage, separately from the collectible price, NFT creation, and network fees. Review the storage quote before approval. Later collectors reuse the published files; funding publication grants no additional rights.</small>}
        {showMintProgress ? <MintProgress steps={getOfferMintStages(completedStages)} busy={busy}
            status={message || (!wallet.connected ? 'Connect your wallet to mint.' : soldOut ? 'Sold out' : '')} />
            : (message || !wallet.connected) && <p role="status">{message || 'Connect your wallet to mint.'}</p>}
    </div>;
}
